import { Injectable, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { GlobalService } from './global.service';

@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {
  private eventSubscriptions: Subscription[] = [];
  private lastActivityTime = Date.now();
  private checkInterval: any = null;

  // Timeouts por tipo de utilizador (em minutos)
  private readonly TIMEOUT_CLIENT = 15; // Clientes: 15 min
  private readonly TIMEOUT_INTERMEDIARY = 60; // Mediadores/Comerciais: 60 min
  private readonly TIMEOUT_ADMIN = 30; // Admins: 30 min
  private readonly WARNING_BEFORE = 2; // Aviso 2 min antes

  public showInactivityWarning = false;
  public secondsUntilLogout = 0;
  private currentTimeoutMinutes = 15;

  /**
   * Flag para pausar a monitorização durante o fluxo de login.
   * Evita que o IdleService dispare logout() enquanto o utilizador
   * está a autenticar-se, prevenindo race conditions com saveSession().
   */
  public paused = false;

  constructor(
    private authService: AuthService,
    private globalService: GlobalService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.startMonitoring();
    }
  }

  /**
   * Calcula o timeout baseado no tipo de utilizador
   */
  private getTimeoutForUser(): number {
    const user = this.globalService.loggedUserClass;

    if (!user) {
      return this.TIMEOUT_CLIENT;
    }

    // Mediadores e Comerciais: 60 min
    if (user.IntermediaryArea || user.CommercialArea) {
      return this.TIMEOUT_INTERMEDIARY;
    }

    // Admins: 30 min
    if (user.Admin || user.SiteManagement) {
      return this.TIMEOUT_ADMIN;
    }

    // Clientes (default): 15 min
    return this.TIMEOUT_CLIENT;
  }

  private startMonitoring() {
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

    events.forEach(event =>
      this.eventSubscriptions.push(
        fromEvent(window, event)
          .pipe(debounceTime(300))
          .subscribe(() => {
            if (this.paused || this.isOnLoginPage()) return;
            if (!this.isLoggedIn()) return;

            const now = Date.now();
            const timeoutMs = this.getTimeoutForUser() * 60 * 1000;

            // Sync com outras abas: atualizar localStorage no máximo a cada 5 segundos para evitar spam
            if (now - this.lastActivityTime > 5000) {
              try {
                localStorage.setItem('sv_last_activity', now.toString());
              } catch (e) {}
            }

            const elapsed = now - this.lastActivityTime;

            // Se o tempo limite já passou enquanto a aba estava inativa, forçar logout imediato
            if (elapsed >= timeoutMs) {
              this.performLogout();
              return;
            }

            this.lastActivityTime = now;
            this.resetWarning();
          }),
      ),
    );

    this.setupIdleDetection();
  }

  private setupIdleDetection() {
    this.lastActivityTime = Date.now();
    this.currentTimeoutMinutes = this.getTimeoutForUser();

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(() => {
      if (this.paused || this.isOnLoginPage()) return;
      if (!this.isLoggedIn()) return;

      this.currentTimeoutMinutes = this.getTimeoutForUser();
      const now = Date.now();

      // Ler do localStorage para verificar se o utilizador esteve ativo noutra aba
      try {
        const globalLastActivity = localStorage.getItem('sv_last_activity');
        if (globalLastActivity) {
          const globalTime = parseInt(globalLastActivity, 10);
          if (!isNaN(globalTime) && globalTime > this.lastActivityTime && globalTime <= now) {
            this.lastActivityTime = globalTime;
            this.resetWarning();
          }
        }
      } catch (e) {}

      const elapsed = now - this.lastActivityTime;
      const timeoutMs = this.currentTimeoutMinutes * 60 * 1000;
      const warningStartMs = (this.currentTimeoutMinutes - this.WARNING_BEFORE) * 60 * 1000;

      if (elapsed >= timeoutMs) {
        this.performLogout();
      } else if (elapsed >= warningStartMs) {
        this.showInactivityWarning = true;
        this.secondsUntilLogout = Math.ceil((timeoutMs - elapsed) / 1000);
      } else {
        if (this.showInactivityWarning) {
          this.resetWarning();
        }
      }
    }, 1000);
  }

  private isLoggedIn(): boolean {
    return this.authService.isLoggedin(this.globalService.loggedUserClass);
  }

  /**
   * Verifica se a rota atual é a página de login.
   * Nesse caso, a monitorização de inatividade deve ser ignorada
   * para evitar interferência com o fluxo de autenticação.
   */
  private isOnLoginPage(): boolean {
    try {
      return this.router.url.startsWith('/login');
    } catch {
      return false;
    }
  }

  private resetWarning() {
    if (this.showInactivityWarning) {
      console.log('[IdleService] Atividade detectada - cancelando logout');
      this.showInactivityWarning = false;
    }
  }

  private performLogout() {
    console.log('[IdleService] Sessão terminada por inatividade');
    this.resetWarning();
    this.authService.logout();
  }

  public dismissWarning() {
    this.lastActivityTime = Date.now();
    this.resetWarning();
  }

  /**
   * Reset do timer de inatividade. Chamado após login bem-sucedido
   * para garantir que o contador começa do zero.
   */
  public resetIdleTimer(): void {
    this.lastActivityTime = Date.now();
    this.paused = false;
    this.resetWarning();
  }

  // Debug methods
  public showWarning(): void {
    if (this.showInactivityWarning) return; // Já está ativo
    this.showInactivityWarning = true;
    this.secondsUntilLogout = this.WARNING_BEFORE * 60;
  }

  public getFormattedTime(): string {
    const minutes = Math.floor(Math.max(0, this.secondsUntilLogout) / 60);
    const seconds = Math.floor(Math.max(0, this.secondsUntilLogout) % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Obtém o timeout atual em minutos (para debug/display)
   */
  public getCurrentTimeoutMinutes(): number {
    return this.currentTimeoutMinutes;
  }

  ngOnDestroy(): void {
    this.eventSubscriptions.forEach(sub => sub.unsubscribe());
    this.eventSubscriptions = [];
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
}
